import React, { FC, FormEvent, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SEO } from 'components/seo/SEO';
import { breadcrumbJsonLd } from 'components/seo/organizationSchema';
import routeManifest from 'routes/routeManifest.json';
import { fetchPlans, formatPrice, GiftPlan, PlanId, planName, startCheckout } from './giftApi';
import './Gift.css';

const route = routeManifest.find((r) => r.path === '/gift')!;
const breadcrumbs = breadcrumbJsonLd([
  { name: 'Home', path: '/' },
  { name: 'Gift Toastie Pro', path: '/gift' },
]);

const DRAFT_KEY = 'toastie-gift-draft';
const MESSAGE_MAX = 500;

type Delivery = 'them' | 'me';
type When = 'now' | 'date';

interface Draft {
  plan: PlanId;
  delivery: Delivery;
  when: When;
  sendOn: string;
  recipientName: string;
  recipientEmail: string;
  message: string;
  purchaserName: string;
  purchaserEmail: string;
}

const EMPTY: Draft = {
  plan: '12m',
  delivery: 'them',
  when: 'now',
  sendOn: '',
  recipientName: '',
  recipientEmail: '',
  message: '',
  purchaserName: '',
  purchaserEmail: '',
};

const toIsoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const loadDraft = (): Draft => {
  try {
    const saved = sessionStorage.getItem(DRAFT_KEY);
    return saved ? { ...EMPTY, ...JSON.parse(saved) } : EMPTY;
  } catch {
    return EMPTY;
  }
};

const FAQS = [
  {
    q: 'How do they redeem it?',
    a: "The gift email has a code and a Redeem button. They sign in to Toastie (or create a free account) on our website, and Pro is unlocked in the app on that account. It works on iPhone and Android.",
  },
  {
    q: 'Is this a subscription?',
    a: "No. It's a one-off payment. When the gift ends, nothing renews and no one is charged again.",
  },
  {
    q: 'What if they already subscribe to Toastie?',
    a: "They'll need to turn off auto-renew in the App Store or Google Play first. They keep Pro for the time they've already paid for, and the gift starts after that. Codes are valid for 3 years, so there's no rush.",
  },
  {
    q: 'Can I give it in person?',
    a: "Yes. Choose \"Email it to me\" and we'll send the code to you, so you can print it or write it in a card.",
  },
];

export const Gift: FC = () => {
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled') === '1';

  const [plans, setPlans] = useState<GiftPlan[] | null>(null);
  const [plansError, setPlansError] = useState(false);
  const [form, setForm] = useState<Draft>(loadDraft);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const today = useMemo(() => new Date(), []);
  const minDate = toIsoDate(today);
  const maxDate = toIsoDate(new Date(today.getFullYear() + 1, today.getMonth(), today.getDate()));

  useEffect(() => {
    fetchPlans().then(setPlans).catch(() => setPlansError(true));
  }, []);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setError(null);
  };

  const selected = plans?.find((p) => p.plan === form.plan);
  const monthly = plans?.find((p) => p.months === 3);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    if (form.delivery === 'them' && form.when === 'date' && !form.sendOn) {
      setError('Please choose a delivery date.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch { /* fine */ }

    try {
      const toThem = form.delivery === 'them';
      const url = await startCheckout({
        plan: form.plan,
        purchaser_name: form.purchaserName.trim(),
        purchaser_email: form.purchaserEmail.trim(),
        recipient_name: toThem ? form.recipientName.trim() : '',
        recipient_email: toThem ? form.recipientEmail.trim() : '',
        message: toThem ? form.message.trim() : '',
        send_on: toThem && form.when === 'date' ? form.sendOn : '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      window.location.assign(url);
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  };

  return (
    <div className='gift-page'>
      <SEO title={route.title} description={route.description} path={route.path} jsonLd={[breadcrumbs]} />

      <header className='gift-hero'>
        <p className='gift-eyebrow'>Gift Toastie Pro</p>
        <h1 className='gift-title'>Give the gift of feeling understood</h1>
        <p className='gift-lede'>
          Help someone you care about track symptoms, spot patterns and walk into appointments prepared.
          A one-off gift, delivered by email.
        </p>
      </header>

      <form className='gift-card' onSubmit={handleSubmit} noValidate={false}>
        {cancelled && (
          <p className='gift-notice info' role='status'>
            Payment cancelled. Nothing was charged, and your details are still here.
          </p>
        )}

        {/* 1. Plan */}
        <fieldset className='gift-section'>
          <legend className='gift-step'><span>1</span> Choose a gift</legend>
          {plansError ? (
            <p className='gift-notice error'>We couldn't load prices just now. Please refresh the page.</p>
          ) : !plans ? (
            <p className='gift-muted'>Loading prices…</p>
          ) : (
            <div className='gift-plans' role='radiogroup' aria-label='Gift length'>
              {plans.map((p) => {
                const perMonth = p.amount / p.months;
                const saving = monthly && p.months > 3
                  ? Math.round((1 - perMonth / (monthly.amount / monthly.months)) * 100)
                  : 0;
                return (
                  <label key={p.plan} className={`gift-plan ${form.plan === p.plan ? 'selected' : ''}`}>
                    <input
                      type='radio'
                      name='plan'
                      value={p.plan}
                      checked={form.plan === p.plan}
                      onChange={() => set('plan', p.plan)}
                    />
                    {p.months === 12 && <span className='gift-badge'>Best value</span>}
                    <span className='gift-plan-length'>{planName(p.months)}</span>
                    <span className='gift-plan-price'>{formatPrice(p.amount, p.currency)}</span>
                    <span className='gift-plan-sub'>
                      {saving > 0 ? `Save ${saving}%` : 'of Toastie Pro'}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </fieldset>

        {/* 2. Delivery */}
        <fieldset className='gift-section'>
          <legend className='gift-step'><span>2</span> How should we deliver it?</legend>
          <div className='gift-toggle' role='radiogroup'>
            <label className={form.delivery === 'them' ? 'selected' : ''}>
              <input type='radio' name='delivery' checked={form.delivery === 'them'} onChange={() => set('delivery', 'them')} />
              Email it to them
            </label>
            <label className={form.delivery === 'me' ? 'selected' : ''}>
              <input type='radio' name='delivery' checked={form.delivery === 'me'} onChange={() => set('delivery', 'me')} />
              Email it to me to give myself
            </label>
          </div>

          {form.delivery === 'them' ? (
            <>
              <div className='gift-row'>
                <div className='gift-field'>
                  <label htmlFor='recipient-name'>Their name <span className='gift-optional'>(optional)</span></label>
                  <input id='recipient-name' className='rounded-input-box' maxLength={100}
                    value={form.recipientName} onChange={(e) => set('recipientName', e.target.value)} />
                </div>
                <div className='gift-field'>
                  <label htmlFor='recipient-email'>Their email</label>
                  <input id='recipient-email' className='rounded-input-box' type='email' required autoComplete='off'
                    value={form.recipientEmail} onChange={(e) => set('recipientEmail', e.target.value)} />
                </div>
              </div>

              <div className='gift-field'>
                <label htmlFor='gift-message'>Personal message <span className='gift-optional'>(optional)</span></label>
                <textarea id='gift-message' className='rounded-textarea gift-textarea' maxLength={MESSAGE_MAX}
                  value={form.message} onChange={(e) => set('message', e.target.value)} />
                <span className='gift-count'>{form.message.length}/{MESSAGE_MAX}</span>
              </div>

              <div className='gift-field'>
                <span className='gift-label'>When should it arrive?</span>
                <div className='gift-toggle' role='radiogroup'>
                  <label className={form.when === 'now' ? 'selected' : ''}>
                    <input type='radio' name='when' checked={form.when === 'now'} onChange={() => set('when', 'now')} />
                    Right away
                  </label>
                  <label className={form.when === 'date' ? 'selected' : ''}>
                    <input type='radio' name='when' checked={form.when === 'date'} onChange={() => set('when', 'date')} />
                    On a date
                  </label>
                </div>
                {form.when === 'date' && (
                  <>
                    <input className='rounded-input-box gift-date' type='date' min={minDate} max={maxDate}
                      value={form.sendOn} onChange={(e) => set('sendOn', e.target.value)} required
                      aria-label='Delivery date' />
                    <span className='gift-hint'>We'll send it around 8am your time on that day.</span>
                  </>
                )}
              </div>
            </>
          ) : (
            <p className='gift-muted'>
              We'll email the gift code to you, ready to forward, print or write in a card.
            </p>
          )}
        </fieldset>

        {/* 3. Buyer */}
        <fieldset className='gift-section'>
          <legend className='gift-step'><span>3</span> Your details</legend>
          <div className='gift-row'>
            <div className='gift-field'>
              <label htmlFor='purchaser-name'>Your name <span className='gift-optional'>(shown on the gift)</span></label>
              <input id='purchaser-name' className='rounded-input-box' autoComplete='name' maxLength={100}
                value={form.purchaserName} onChange={(e) => set('purchaserName', e.target.value)} />
            </div>
            <div className='gift-field'>
              <label htmlFor='purchaser-email'>Your email</label>
              <input id='purchaser-email' className='rounded-input-box' type='email' required autoComplete='email'
                value={form.purchaserEmail} onChange={(e) => set('purchaserEmail', e.target.value)} />
              <span className='gift-hint'>For your receipt and a copy of the code.</span>
            </div>
          </div>
        </fieldset>

        {error && <p className='gift-notice error' role='alert'>{error}</p>}

        <button className='button gift-submit' type='submit' disabled={!selected || submitting}>
          {submitting
            ? 'Taking you to payment…'
            : selected
              ? `Continue to payment · ${formatPrice(selected.amount, selected.currency)}`
              : 'Continue to payment'}
        </button>

        <p className='gift-smallprint'>
          One-off payment, no subscription. Codes are valid for 3 years. Secure payment by Stripe.
        </p>
      </form>

      <section className='gift-faq'>
        <h2>Questions</h2>
        {FAQS.map((f) => (
          <details key={f.q}>
            <summary>{f.q}</summary>
            <p>{f.a}</p>
          </details>
        ))}
      </section>
    </div>
  );
};
